import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, FileText, Stethoscope, HelpCircle, ShoppingBag, User } from "lucide-react";

const STATUS_LABEL = {
  assigned: { label: "Betalt", color: "bg-sky-50 text-sky-700" },
  received: { label: "Enhet mottatt", color: "bg-indigo-50 text-indigo-700" },
  in_progress: { label: "Under reparasjon", color: "bg-indigo-50 text-indigo-700" },
  repair_done: { label: "Reparasjon fullført", color: "bg-emerald-50 text-emerald-700" },
  sent_back: { label: "Sendt tilbake", color: "bg-emerald-50 text-emerald-700" },
  completed: { label: "Fullført", color: "bg-emerald-100 text-emerald-800" },
  paid_out: { label: "Utbetalt", color: "bg-slate-100 text-slate-700" },
  diagnosis_ready: { label: "Diagnose klar – vurder", color: "bg-orange-50 text-orange-700" },
  approved_awaiting_extra_payment: { label: "Godkjent – betal reparasjon", color: "bg-amber-50 text-amber-700" },
  declined_returning: { label: "Avslått", color: "bg-slate-100 text-slate-600" },
};

const ACTIVE_STATUSES = new Set([
  "assigned", "received", "in_progress", "repair_done",
  "diagnosis_ready", "approved_awaiting_extra_payment",
]);

/**
 * Stateless row that renders one customer order in the list. Defined at module
 * level (not inside `MyOrders`) so React's reconciler treats it as a stable
 * component type — prevents the whole subtree from re-mounting on each parent
 * re-render and preserves any internal state.
 */
function OrderRow({ o, compact, onDecide, onPayExtra }) {
  const st = STATUS_LABEL[o.status] || { label: o.status, color: "bg-slate-50 text-slate-600" };
  return (
    <div className="tk-card" data-testid={`order-row-${o.id}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link to={`/bestilling/${o.id}`} className="text-xs text-slate-400 hover:text-[#0B1C30] font-mono" data-testid={`track-link-${o.id}`}>
              #{o.id.slice(0, 8)} ↗
            </Link>
            {o.is_diagnostic && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#4A7C59]/10 text-[#4A7C59]">
                <Stethoscope size={10} /> Diagnose
              </span>
            )}
          </div>
          <h3 className="font-semibold text-lg leading-snug truncate">{o.product_name}</h3>
          <p className="text-sm text-slate-500">{o.service_name} · {o.fixer_display_name}</p>
          {!compact && o.is_diagnostic && o.symptoms?.length > 0 && (
            <p className="text-xs text-slate-500 mt-1">Symptomer: {o.symptoms.join(", ")}</p>
          )}
        </div>
        <div className="flex items-center gap-6 shrink-0">
          <div className="text-right">
            <p className="text-xs text-slate-400">Total</p>
            <p className="font-semibold">kr {Number(o.total_price).toFixed(0)}</p>
          </div>
          <span className={`text-xs px-3 py-1 rounded-md ${st.color}`}>{st.label}</span>
        </div>
      </div>

      {!compact && o.is_diagnostic && o.status === "diagnosis_ready" && (
        <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-testid={`diagnosis-panel-${o.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <FileText size={16} className="text-[#4A7C59]" />
            <h4 className="font-semibold">Verkstedets diagnose</h4>
          </div>
          <p className="text-sm text-slate-700 mb-1">
            <span className="text-slate-500">Funn:</span> {o.diagnostic_findings}
          </p>
          <p className="text-sm text-slate-700 mb-1">
            <span className="text-slate-500">Anbefalt reparasjon:</span> {o.recommended_repair}
          </p>
          <p className="text-sm text-slate-700 mb-4">
            <span className="text-slate-500">Tilleggspris:</span>{" "}
            <span className="font-semibold">kr {Number(o.recommended_price || 0).toFixed(0)}</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onDecide(o, "approve")} className="tk-btn-accent !py-2 !px-4 text-sm" data-testid={`approve-diag-${o.id}`}>
              <CheckCircle2 size={16} /> Godkjenn & betal kr {Number(o.recommended_price || 0).toFixed(0)}
            </button>
            <button onClick={() => onDecide(o, "decline")} className="tk-btn-secondary !py-2 !px-4 text-sm" data-testid={`decline-diag-${o.id}`}>
              <XCircle size={16} /> Avslå – send tilbake
            </button>
          </div>
        </div>
      )}

      {!compact && o.is_diagnostic && o.status === "approved_awaiting_extra_payment" && (
        <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
          <button onClick={() => onPayExtra(o)} className="tk-btn-primary !py-2 !px-4 text-sm" data-testid={`pay-extra-${o.id}`}>
            Betal kr {Number(o.recommended_price || 0).toFixed(0)} for reparasjon
          </button>
        </div>
      )}

      {!compact && o.is_diagnostic && o.status === "declined_returning" && o.shipping_label_url && (
        <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-sm">
          <a
            href={o.shipping_label_url}
            target="_blank"
            rel="noreferrer"
            className="text-[#0B1C30] underline"
            data-testid={`return-label-${o.id}`}
          >
            Last ned returetikett (Bring) →
          </a>
        </div>
      )}
    </div>
  );
}

export default function MyOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  const load = useCallback(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = useCallback(async (o, decision) => {
    try {
      if (decision === "decline") {
        await api.post(`/orders/${o.id}/diagnostic-decision`, { decision: "decline" });
        toast.success("Diagnose avslått – enheten sendes tilbake.");
        load();
        return;
      }
      // APPROVE — mark decision approved AND create an approval draft, then
      // send the user through the universal checkout (with method picker).
      await api.post(`/orders/${o.id}/diagnostic-decision`, { decision: "approve" });
      const { data: draft } = await api.post("/checkout/draft/diagnostic-approval", { order_id: o.id });
      nav(`/kassen?draft=${draft.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [load, nav]);

  // Continue paying for an already-approved diagnostic that didn't pay yet.
  const continuePayment = useCallback(async (o) => {
    try {
      const { data: draft } = await api.post("/checkout/draft/diagnostic-approval", { order_id: o.id });
      nav(`/kassen?draft=${draft.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [nav]);

  const active = orders.filter((o) => ACTIVE_STATUSES.has(o.status));
  const history = orders.filter((o) => !ACTIVE_STATUSES.has(o.status));

  return (
    <div className="tk-container py-16" data-testid="my-orders-page">
      <p className="tk-overline mb-3">Mine bestillinger</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-10">Reparasjoner</h1>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="customer-quick-actions">
        <Link to="/butikk" className="tk-card tk-card-interactive flex items-center gap-3" data-testid="qa-shop">
          <div className="w-10 h-10 rounded-md bg-[#0B1C30]/5 flex items-center justify-center"><ShoppingBag size={18} /></div>
          <div>
            <p className="font-semibold leading-snug">Bestill ny reparasjon</p>
            <p className="text-xs text-slate-500">Bla i deler og book verksted</p>
          </div>
        </Link>
        <Link to="/diagnose" className="tk-card tk-card-interactive flex items-center gap-3" data-testid="qa-diagnose">
          <div className="w-10 h-10 rounded-md bg-[#4A7C59]/10 flex items-center justify-center"><HelpCircle size={18} className="text-[#4A7C59]" /></div>
          <div>
            <p className="font-semibold leading-snug">Usikker på hva som er galt?</p>
            <p className="text-xs text-slate-500">Bestill en diagnose</p>
          </div>
        </Link>
        <Link to="/konto" className="tk-card tk-card-interactive flex items-center gap-3" data-testid="qa-account">
          <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center"><User size={18} /></div>
          <div>
            <p className="font-semibold leading-snug">Mine opplysninger</p>
            <p className="text-xs text-slate-500">Oppdater navn, telefon og adresse</p>
          </div>
        </Link>
      </div>

      {/* Active orders */}
      <h2 className="text-xl font-semibold mb-4" data-testid="active-orders-heading">
        Aktive bestillinger <span className="text-slate-400 font-normal">({active.length})</span>
      </h2>
      {active.length === 0 ? (
        <p className="text-slate-500 mb-12">Ingen aktive bestillinger.</p>
      ) : (
        <div className="space-y-4 mb-12">
          {active.map((o) => <OrderRow key={o.id} o={o} onDecide={decide} onPayExtra={continuePayment} />)}
        </div>
      )}

      {/* History */}
      <h2 className="text-xl font-semibold mb-4" data-testid="history-heading">
        Tidligere reparasjoner <span className="text-slate-400 font-normal">({history.length})</span>
      </h2>
      {history.length === 0 ? (
        <p className="text-slate-500">Ingen tidligere reparasjoner ennå.</p>
      ) : (
        <div className="space-y-4">
          {history.map((o) => <OrderRow key={o.id} o={o} compact onDecide={decide} onPayExtra={continuePayment} />)}
        </div>
      )}
    </div>
  );
}
